import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { cloneDeep } from 'lodash-es'
import { apiClientV1 } from '@/ContextTab/services/apiClient'
import type { DataOfUserStatusListGetOut } from '@/api/generated/v1'


// TODO: заменить на корректный тип из api v2,
//  в v1 он некорректен UsersNotInGroupsOfDataOfGroupAndUserListGetOut

type V1User = {
  asoi_mailbox_id: number
  code: string
  first_name: string | null
  id: number
  is_active: boolean
  is_blocked: boolean
  last_name: string | null
  name: string
  patronymic: string | null
}

type V1Group = {
  code: string
  id: number
  is_hidden: boolean
  is_mail: boolean
  name: string
  users: { user: V1User }[]
}

// as got from api
export type V1GroupsUsersResponse = {
  groups_with_users: V1Group[]
  users_not_in_groups: V1User[]
}

// internal tree item
// TODO: Это следует переработать:
//  - разбить на объединение двух интерфейсов - групп и пользователя
//  - выкинуть дублирующую информацию вроде is_group/id:123G/isGroup
export interface IAuthorTreeItem {
  id: number | string // "123G"
  uid: string
  asoi_mailbox_id?: number
  code: string
  first_name?: string | null
  patronymic?: string | null
  last_name?: string | null
  is_active?: boolean
  is_blocked?: boolean
  is_online?: boolean
  last_seen?: string | null
  name: string
  pos: number
  isUser: boolean
  isGroup: boolean
  track: (number | string)[] // id[] | uid[]
  children: (number | string)[] // id[] | uid[]
  is_mail?: boolean
  is_hidden?: boolean
  is_group?: boolean
  image?: string
}

export function processGroupsAndUsers(groupsAndUsers: V1GroupsUsersResponse) {
  /* Раскладываем группы и пользователей по двум словарям:
     1. По id. Каждая группа и пользователь добавляется однократно (пользователей,
      состоящих в нескольких группах, добавляем с первой из почтовых групп, в которой они состоят).
     2. По uid. Пользователи, состоящие в нескольких группах, добавляются несколько раз с каждой из групп,
      в которой состоят.
  */
  const itemsById: Record<string | number, IAuthorTreeItem> = {}
  const itemsByUid: Record<string, IAuthorTreeItem> = {}

  let pos = 0
  const adminGroups: V1Group[] = []

  function addUser(apiUser: V1User, group?: IAuthorTreeItem): IAuthorTreeItem {
    const user: IAuthorTreeItem = {
      ...apiUser,
      pos: pos++,
      isUser: true,
      isGroup: false,
      children: [],
      uid: globalThis.crypto.randomUUID(),
      track: [],
    }

    if (!itemsById[user.id]) {
      itemsById[user.id] = {
        ...user,
        track: group ? [0, group.id] : [0],
      }
    }

    itemsByUid[user.uid] = {
      ...user,
      track: group ? [0, group.uid] : [0],
    }

    return user
  }

  groupsAndUsers?.groups_with_users?.forEach((apiGroup) => {
    if (!apiGroup.users.length) {
      return
    }

    const group: IAuthorTreeItem = {
      ...apiGroup,
      track: [0],
      pos: pos++,
      isGroup: true,
      isUser: false,
      id: apiGroup.id + 'G', // вот зачем?!
      uid: globalThis.crypto.randomUUID(),
      children: [],
    }

    // Административные группы не добавляем,
    // но собираем отдельно и берем из них только тех пользователей,
    // которые не состоят ни в одной из почтовых групп.
    if (apiGroup.is_mail) {
      const addedUsers = apiGroup.users.map(({ user }) => addUser(user, group))
      itemsById[group.id] = { ...group, children: [...addedUsers.map((user) => user.id)] }
      itemsByUid[group.uid] = { ...group, children: [...addedUsers.map((user) => user.uid)] }
    } else {
      adminGroups.push(apiGroup)
    }
  })

  adminGroups.forEach((item) =>
    item.users.forEach(({ user }) => {
      if (!(user.id in itemsById)) {
        addUser(user)
      }
    }),
  )

  groupsAndUsers?.users_not_in_groups?.forEach((user) => addUser(user))

  return { itemsById, itemsByUid }
}

export const useGroupsAndUsersStore = defineStore('groupsAndUsers', () => {

  const groupsAndUsers = ref<V1GroupsUsersResponse>({
    groups_with_users: [],
    users_not_in_groups: [],
  })
  const groupAndUsersById = ref<Record<number | string, IAuthorTreeItem>>({})
  const groupAndUsersByUid = ref<Record<string, IAuthorTreeItem>>({})
  const userStatuses = ref<DataOfUserStatusListGetOut[]>([])

  const recipients = computed<IAuthorTreeItem[]>(() => {
    const _groupsAndUsersById = cloneDeep(groupAndUsersById.value)

    userStatuses.value.forEach((userItem) => {
      const user = _groupsAndUsersById[userItem.user_id]

      if (user) {
        user.is_online = userItem.is_online
        user.last_seen = userItem.last_seen
      }
    })

    const recipients = Object.values(_groupsAndUsersById).filter((item) => {
      if (item.isGroup) {
        return item.is_mail && !item.is_hidden
      } else {
        return item.asoi_mailbox_id !== null && item.is_active
      }
    })

    type UserLike = { name: string, [key: string]: any }

    const sortFn = (a: UserLike, b: UserLike) => {
      const nameA = a.name.toLowerCase()
      const nameB = b.name.toLowerCase()
      if (nameA < nameB) {
        return -1
      }

      if (nameA > nameB) {
        return 1
      }
      return 0
    }

    const groups = recipients
      .filter(({ isGroup }) => isGroup)
      .map((el) => ({
        ...el,
        name: el.name,
        is_group: true,
        id: Number(String(el.id).replace('G', '')), // зачем?!
      }))
      .sort(sortFn)

    const users = recipients
      .filter(({ isUser }) => isUser)
      .map((el) => ({ ...el, name: el.name, is_group: false }))
      .sort(sortFn)

    return [...groups, ...users]
  })

  const groupsAndNonBlockedUsersByUid = computed(() => {
    const filtered: Record<string, IAuthorTreeItem> = {}
    Object.entries(groupAndUsersByUid.value ?? {})
      .forEach(([uid, item]) => {
        if (item.isUser && item.is_blocked) {
          return
        }
        filtered[uid] = item
      })
    return filtered
  })

  async function fetchData() {
    // TODO: метод из v2 отличается по ответу от v1, мигрировать аккуратно
    const response = await apiClientV1.UsersService.getGroupsAndUsers()

    if (response?.data?.data) {
      groupsAndUsers.value = response.data.data as any
      const { itemsById, itemsByUid } = processGroupsAndUsers(response.data.data as any)
      groupAndUsersById.value = itemsById
      groupAndUsersByUid.value = itemsByUid
      return response.data.data
    }

    return null
  }

  async function fetchUserStatuses() {
    const response = await apiClientV1.UsersService.getUsersStatuses()

    if (response?.data?.data) {
      userStatuses.value = response.data.data
    }
  }

  return {
    groupsAndUsers, // -> legacy
    groupAndUsersById,
    groupAndUsersByUid,
    groupsAndNonBlockedUsersByUid,
    recipients,

    fetchData,
    fetchUserStatuses,
  }
})
